import { useState, useEffect } from "react";
import { defaultInspectionWizardForm } from "./inspectionWizardConfig";
import API_BASE_URL from "../../../../../../Constants";

const useInspectionWizard = (building) => {
  // Local States to manage the inspection wizard
  const [agency, setAgency] = useState({});
  const [agencyTests, setAgencyTests] = useState([]);
  const [devices, setDevices] = useState([]);
  const [formData, setFormData] = useState(defaultInspectionWizardForm);
  const [scheduledInspections, setScheduledInspections] = useState([]);
  const [devicesWithoutInspection, setDevicesWithoutInspection] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch agency and agency tests
  useEffect(() => {
    // Skip fetching if building is undefined or null
    if (!building || !building.agencyID) {
      setAgency({});
      setAgencyTests([]);
      return;
    }

    const fetchAgency = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencies/${building.agencyID}`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch agency: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        setAgency(data);
      } catch (error) {
        console.error("Error fetching agency:", error);
        setAgency({}); // Set empty object on error
      }
    };

    const fetchAgencyTests = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${building.agencyID}`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch agency tests: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        setAgencyTests(data);
      } catch (error) {
        console.error("Error fetching agency tests:", error);
        setAgencyTests([]); // Set empty array on error
      }
    };

    fetchAgency();
    fetchAgencyTests();
  }, [building?.agencyID]); // Only depend on building.agencyID, not the entire building object

  // Fetch devices
  useEffect(() => {
    // Skip fetching if building is undefined or null
    if (!building || !building.buildingID) {
      setDevices([]);
      return;
    }

    const fetchDevices = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/setup/buildings/devices/${building.buildingID}`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch devices: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        setDevices(data);
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDevices([]); // Set empty array on error
      }
    };

    fetchDevices();
  }, [building?.buildingID]); // Only depend on building.buildingID, not the entire building object

  // Fetch scheduled inspections when year changes
  useEffect(() => {
    // Skip fetching if building is undefined or null or no inspYear
    if (!building?.buildingID || !formData.inspYear) {
      setScheduledInspections([]);
      return;
    }

    const fetchScheduledInspections = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inspections/scheduledInspections?buildingId=${building.buildingID}&year=${formData.inspYear}&agencyId=${building.agencyID}`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch scheduled inspections: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        setScheduledInspections(data);

        // After fetching scheduled inspections, identify devices without inspections
        analyzeDevicesCoverage(data);
      } catch (error) {
        console.error("Error fetching scheduled inspections:", error);
        setScheduledInspections([]);
        setDevicesWithoutInspection([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScheduledInspections();
  }, [building?.buildingID, formData.inspYear, building?.agencyID]);

  // Analyze which devices are not covered by existing inspections
  const analyzeDevicesCoverage = (scheduledInspections) => {
    // If no devices or no scheduled inspections, all devices need inspection
    if (!devices.length) {
      setDevicesWithoutInspection([]);
      return;
    }

    // Extract all device IDs that are already covered by scheduled inspections
    const coveredDeviceIds = new Set();
    scheduledInspections.forEach((inspection) => {
      if (inspection.devices && Array.isArray(inspection.devices)) {
        inspection.devices.forEach((device) => {
          coveredDeviceIds.add(device.deviceId);
        });
      }
    });

    // Find devices that are not covered
    const uncoveredDevices = devices.filter(
      (device) => !coveredDeviceIds.has(device.deviceId)
    );

    setDevicesWithoutInspection(uncoveredDevices);
  };

  // Return the values to make them available to the component
  return {
    agency,
    agencyTests,
    devices,
    formData,
    setFormData,
    scheduledInspections,
    devicesWithoutInspection,
    isLoading,
  };
};

export default useInspectionWizard;
